import React, { useEffect, useState } from 'react';
import { Modal, Row, Col, Button, Badge } from 'react-bootstrap';
import { Link, useLocation } from 'react-router-dom';
import { useProductDwell } from '../hooks/useProductDwell';
import { fetchTimeRecommendations } from '../utils/productTracking';
import { useProductRatings } from '../hooks/useProductRatings';
import StarRating from './product/StarRating';

/* Motes of light drifting up the modal. Purely decorative — spread by hand
   so they never rise in a visible column. */
const QV_SPARKS = [
  { glyph: '✦', x: '6%', y: '78%', size: '13px', dur: '8s', delay: '0s' },
  { glyph: '✧', x: '21%', y: '92%', size: '9px', dur: '9.5s', delay: '1.8s' },
  { glyph: '✦', x: '44%', y: '84%', size: '11px', dur: '8.6s', delay: '3.4s' },
  { glyph: '✧', x: '63%', y: '95%', size: '12px', dur: '10s', delay: '1.1s' },
  { glyph: '✦', x: '81%', y: '80%', size: '10px', dur: '9s', delay: '4.2s' },
  { glyph: '✧', x: '94%', y: '90%', size: '14px', dur: '8.2s', delay: '2.6s' }
];

function QuickViewModal({ product, show, onHide }) {
  /* Track dwell only while the modal is actually open */
  useProductDwell(product?._id, 'quickview', show);

  const location = useLocation();
  const isAdmin = JSON.parse(
    localStorage.getItem('userInfo') || 'null'
  )?.isAdmin;

  const [suggestions, setSuggestions] = useState([]);

  const { getRating } = useProductRatings();

  const rating = getRating(product?._id);

  useEffect(() => {
    if (!show) {
      return;
    }

    let cancelled = false;

    fetchTimeRecommendations().then((recs) => {
      if (!cancelled) {
        setSuggestions(recs.filter((r) => r._id !== product?._id).slice(0, 3));
      }
    });

    return () => {
      cancelled = true;
    };
  }, [show, product?._id]);

  /* Auto-advancing image slider (every 4s, no arrows) */
  const [slide, setSlide] = useState(0);

  const galleryImages =
    product?.images && product.images.length
      ? product.images
      : product?.image
        ? [product.image]
        : [];

  useEffect(() => {
    setSlide(0);
  }, [product?._id, show]);

  useEffect(() => {
    if (!show || galleryImages.length <= 1) {
      return undefined;
    }
    const timer = setInterval(() => {
      setSlide((s) => (s + 1) % galleryImages.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [show, galleryImages.length]);

  if (!product) return null;

  const oldPrice = Math.round(product.price * 1.2);
  const discount = Math.round(((oldPrice - product.price) / oldPrice) * 100);

  /* What the seller listed about the product — shown only when it exists,
     so the column never pads itself out with empty bullets. */
  const highlights = (product.highlights || []).filter(Boolean).slice(0, 4);

  const stock = product.stock ?? 0;
  const soldOut = stock <= 0;
  const lowStock = !soldOut && stock <= 5;

  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      size="lg"
      contentClassName="quickview-modal"
      backdropClassName="quickview-backdrop"
    >
      <style>{quickViewStyles}</style>

      {/* Divine light: an aurora wash, a drifting star field and motes
          rising through the pane. All decorative. */}
      <span className="qv-aura" aria-hidden="true" />
      <span className="qv-stars" aria-hidden="true" />

      {QV_SPARKS.map((spark, i) => (
        <span
          key={i}
          className="qv-spark"
          aria-hidden="true"
          style={{
            '--x': spark.x,
            '--y': spark.y,
            '--s': spark.size,
            '--d': spark.dur,
            '--delay': spark.delay
          }}
        >
          {spark.glyph}
        </span>
      ))}

      <Modal.Header closeButton className="border-0 pb-0 qv-head" />

      <Modal.Body className="p-4 qv-body">
        <Row className="g-4 align-items-stretch">
          {/* Image */}
          <Col md={6} className="d-flex">
            <div className="quickview-img-wrap">
              <span className="qv-img-rays" aria-hidden="true" />

              {galleryImages.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt={product.name}
                  className={
                    'quickview-img ' +
                    (i === slide ? 'qv-slide-on' : 'qv-slide-off')
                  }
                  onError={(e) => {
                    e.currentTarget.src =
                      'https://placehold.co/600x600?text=No+Image';
                  }}
                />
              ))}
              <span className="qv-img-sheen" aria-hidden="true" />
              <span className="qv-img-glow" aria-hidden="true" />

              <span className="quickview-badge">🔥 Top Sale</span>
              {discount > 0 && (
                <span className="quickview-discount">-{discount}%</span>
              )}
              {galleryImages.length > 1 && (
                <div className="quickview-dots">
                  {galleryImages.map((_, i) => (
                    <span
                      key={i}
                      className={
                        'quickview-dot ' + (i === slide ? 'quickview-dot-on' : '')
                      }
                    />
                  ))}
                </div>
              )}
            </div>
          </Col>

          {/* Details */}
          <Col md={6} className="d-flex flex-column">
            <h3 className="fw-bold mb-2 quickview-title">{product.name}</h3>

            <div className="mb-2 quickview-stars">
              <StarRating
                average={rating.averageRating}
                count={rating.totalReviews}
                size="0.95rem"
              />
            </div>

            {product.category && (
              <Badge bg="light" text="dark" className="mb-3 quickview-cat">
                {product.category}
              </Badge>
            )}

            <div className="mb-3 d-flex align-items-baseline gap-2 flex-wrap">
              <span className="fw-bold quickview-price">₹{product.price}</span>
              <span className="text-muted text-decoration-line-through">
                ₹{oldPrice}
              </span>
              {discount > 0 && (
                <span className="quickview-save">✨ Save {discount}%</span>
              )}
            </div>

            <p className="text-muted quickview-desc">
              {product.shortDescription}
            </p>

            {/* What the seller called out about this product */}
            {highlights.length > 0 && (
              <ul className="qv-highlights">
                {highlights.map((point, i) => (
                  <li key={i} style={{ '--i': i }}>
                    <span className="qv-highlight-tick" aria-hidden="true">
                      ✦
                    </span>
                    {point}
                  </li>
                ))}
              </ul>
            )}

            {/* Availability, so the modal answers "can I get it?" too */}
            <div
              className={
                'qv-stock' +
                (soldOut ? ' is-out' : lowStock ? ' is-low' : ' is-in')
              }
            >
              <span className="qv-stock-dot" aria-hidden="true" />
              {soldOut
                ? 'Out of stock'
                : lowStock
                  ? `Hurry — only ${stock} left`
                  : 'In stock · ready to ship'}
            </div>

            {/* Everything below is pushed to the foot of the column */}
            <div className="qv-perks mt-auto">
              {[
                { icon: '🚚', label: 'Free delivery' },
                { icon: '↩️', label: '7-day returns' },
                { icon: '🔒', label: 'Secure checkout' }
              ].map((perk) => (
                <span className="qv-perk" key={perk.label}>
                  <span className="qv-perk-icon">{perk.icon}</span>
                  {perk.label}
                </span>
              ))}
            </div>

            <div className="d-flex flex-column flex-sm-row flex-wrap gap-2 mt-3">
              <Button
                as={Link}
                to={`/product/${product._id}`}
                variant="dark"
                className="flex-fill fw-bold quickview-btn qv-btn-primary"
                onClick={onHide}
              >
                <span className="qv-btn-shine" aria-hidden="true" />
                View Full Details →
              </Button>

              {isAdmin && (
                <Button
                  as={Link}
                  to={`/admin/edit-product/${product._id}`}
                  state={{ from: location.pathname + location.search }}
                  variant="warning"
                  className="fw-semibold quickview-btn qv-btn-edit"
                  onClick={onHide}
                >
                  ✏️ Edit
                </Button>
              )}

              <Button
                variant="outline-secondary"
                className="quickview-btn qv-btn-ghost"
                onClick={onHide}
              >
                Close
              </Button>
            </div>
          </Col>
        </Row>

        {suggestions.length > 0 && (
          <div className="quickview-suggest mt-4 pt-3">
            <h6 className="fw-bold mb-3 quickview-suggest-head">
              ✨ Suggested For You
            </h6>
            <Row className="g-3">
              {suggestions.map((item, i) => (
                <Col xs={4} key={item._id}>
                  <Link
                    to={`/product/${item._id}`}
                    className="quickview-suggest-card"
                    onClick={onHide}
                    /* Drives the staggered entrance of the shelf */
                    style={{ '--i': i }}
                  >
                    <img
                      src={item.image}
                      alt={item.name}
                      className="quickview-suggest-img"
                    />
                    <div className="quickview-suggest-name">{item.name}</div>
                    <div className="quickview-suggest-price">₹{item.price}</div>
                  </Link>
                </Col>
              ))}
            </Row>
          </div>
        )}
      </Modal.Body>
    </Modal>
  );
}

const quickViewStyles = `
  /* Backdrop tinted toward deep violet rather than flat black */
  .quickview-backdrop.show {
    background: radial-gradient(120% 90% at 50% 40%, #2e1065, #0b0616 75%);
    opacity: 0.82;
  }

  /* ===== Shell ===== */
  .quickview-modal {
    border: none;
    border-radius: 26px;
    overflow: hidden;
    isolation: isolate;
    background: linear-gradient(170deg, #fffdf8 0%, #fdf7ff 45%, #fff6ec 100%);
    box-shadow: 0 34px 80px rgba(46,16,101,0.45), 0 0 70px rgba(251,191,36,0.18);
    animation: qvAscend 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  }
  @keyframes qvAscend {
    from { opacity: 0; transform: translateY(22px) scale(0.965); }
    to   { opacity: 1; transform: none; }
  }
  /* Luminous rim travelling around the edge */
  .quickview-modal::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: 5;
    border-radius: inherit;
    padding: 1.5px;
    background: linear-gradient(120deg, rgba(251,191,36,0.95), rgba(167,139,250,0.9) 28%, rgba(255,255,255,0.25) 48%, rgba(234,88,12,0.75) 72%, rgba(251,191,36,0.95));
    background-size: 300% 300%;
    -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    -webkit-mask-composite: xor;
    mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
    mask-composite: exclude;
    animation: qvDrift 9s ease-in-out infinite;
    pointer-events: none;
  }
  @keyframes qvDrift {
    0%, 100% { background-position: 0% 50%; }
    50%      { background-position: 100% 50%; }
  }

  /* Aurora breathing behind the content */
  .qv-aura {
    position: absolute;
    inset: -25%;
    pointer-events: none;
    background:
      radial-gradient(380px 260px at 20% 12%, rgba(251,191,36,0.28), transparent 66%),
      radial-gradient(420px 300px at 84% 34%, rgba(167,139,250,0.30), transparent 66%),
      radial-gradient(360px 240px at 44% 92%, rgba(234,88,12,0.20), transparent 66%);
    filter: blur(10px);
    animation: qvAurora 20s ease-in-out infinite alternate;
  }
  @keyframes qvAurora {
    0%   { transform: translate3d(-3%, -2%, 0) scale(1);    opacity: 0.85; }
    50%  { transform: translate3d(3%, 2%, 0)   scale(1.12); opacity: 1; }
    100% { transform: translate3d(-1%, 4%, 0)  scale(1.05); opacity: 0.9; }
  }

  /* Star field that twinkles in and out */
  .qv-stars {
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      radial-gradient(1.6px 1.6px at 11% 22%, rgba(251,191,36,0.9), transparent),
      radial-gradient(1.2px 1.2px at 31% 64%, rgba(167,139,250,0.8), transparent),
      radial-gradient(2px 2px     at 52% 14%, rgba(234,88,12,0.7), transparent),
      radial-gradient(1.4px 1.4px at 69% 48%, rgba(251,191,36,0.8), transparent),
      radial-gradient(1.6px 1.6px at 88% 26%, rgba(167,139,250,0.75), transparent),
      radial-gradient(1.2px 1.2px at 78% 82%, rgba(251,191,36,0.7), transparent);
    animation: qvTwinkle 4.4s ease-in-out infinite alternate;
  }
  @keyframes qvTwinkle {
    from { opacity: 0.3; }
    to   { opacity: 0.95; }
  }

  /* Motes of light rising through the pane */
  .qv-spark {
    position: absolute;
    top: var(--y);
    left: var(--x);
    font-size: var(--s);
    line-height: 1;
    color: #f59e0b;
    text-shadow: 0 0 12px rgba(251,191,36,0.85), 0 0 22px rgba(167,139,250,0.5);
    pointer-events: none;
    opacity: 0;
    animation: qvRise var(--d) ease-in-out var(--delay) infinite;
  }
  @keyframes qvRise {
    0%   { opacity: 0;   transform: translateY(18px) scale(0.5) rotate(0deg); }
    18%  { opacity: 0.9; }
    72%  { opacity: 0.5; }
    100% { opacity: 0;   transform: translateY(-70px) scale(1.2) rotate(200deg); }
  }

  /* Content rides above the celestial layers */
  .qv-head,
  .qv-body {
    position: relative;
    z-index: 1;
  }
  .qv-head .btn-close {
    background-color: rgba(255,255,255,0.85);
    border-radius: 50%;
    padding: 10px;
    box-shadow: 0 4px 14px rgba(76,29,149,0.18);
    transition: transform 0.2s ease, box-shadow 0.2s ease;
  }
  .qv-head .btn-close:hover {
    transform: rotate(90deg) scale(1.08);
    box-shadow: 0 6px 20px rgba(251,191,36,0.5);
  }

  /* ===== Image ===== */
  .quickview-img-wrap {
    position: relative;
    border-radius: 20px;
    overflow: hidden;
    /* Grows to whatever height the details column ends up at, with a floor
       for the stacked mobile layout where there is nothing to match. */
    flex: 1 1 auto;
    width: 100%;
    min-height: clamp(260px, 45vw, 420px);
    background: linear-gradient(160deg, #1e1b4b, #4c1d95 55%, #7c2d12);
    box-shadow: 0 20px 44px rgba(76,29,149,0.32);
  }
  /* Rotating shafts of light behind the product */
  .qv-img-rays {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 190%;
    aspect-ratio: 1 / 1;
    transform: translate(-50%, -50%);
    pointer-events: none;
    opacity: 0.5;
    background: conic-gradient(from 0deg,
      rgba(255,255,255,0.22) 0deg 5deg, transparent 5deg 24deg,
      rgba(251,191,36,0.26) 24deg 30deg, transparent 30deg 52deg,
      rgba(167,139,250,0.20) 52deg 57deg, transparent 57deg 90deg);
    -webkit-mask: radial-gradient(closest-side, rgba(0,0,0,0.95), rgba(0,0,0,0.3) 58%, transparent 82%);
    mask: radial-gradient(closest-side, rgba(0,0,0,0.95), rgba(0,0,0,0.3) 58%, transparent 82%);
    animation: qvRaySpin 40s linear infinite;
  }
  @keyframes qvRaySpin {
    to { transform: translate(-50%, -50%) rotate(360deg); }
  }
  .quickview-img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    opacity: 0;
    transition: opacity 0.8s ease;
  }
  /* The visible slide drifts slowly inward — a slow reveal, not a static shot */
  .qv-slide-on {
    opacity: 1;
    animation: qvKen 8s ease-out both;
  }
  @keyframes qvKen {
    from { transform: scale(1.001); }
    to   { transform: scale(1.09); }
  }
  .qv-slide-off {
    opacity: 0;
  }
  /* A blade of light sweeping across the photo */
  .qv-img-sheen {
    position: absolute;
    top: -50%;
    bottom: -50%;
    left: -40%;
    width: 30%;
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.45), transparent);
    filter: blur(6px);
    transform: rotate(16deg);
    animation: qvSheen 7s ease-in-out infinite;
  }
  @keyframes qvSheen {
    0%        { left: -40%; opacity: 0; }
    12%       { opacity: 1; }
    48%       { opacity: 1; }
    58%, 100% { left: 120%; opacity: 0; }
  }
  /* Golden light pooling at the base of the frame */
  .qv-img-glow {
    position: absolute;
    inset: auto 0 0 0;
    height: 45%;
    z-index: 2;
    pointer-events: none;
    background: linear-gradient(to top, rgba(251,191,36,0.28), transparent 70%);
    animation: qvGlowBreathe 5.5s ease-in-out infinite;
  }
  @keyframes qvGlowBreathe {
    0%, 100% { opacity: 0.5; }
    50%      { opacity: 1; }
  }
  .quickview-dots {
    position: absolute;
    bottom: 12px;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 6px;
    z-index: 3;
  }
  .quickview-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.55);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    transition: all 0.3s ease;
  }
  .quickview-dot-on {
    width: 22px;
    border-radius: 6px;
    background: linear-gradient(90deg, #fde68a, #fbbf24);
    box-shadow: 0 0 12px rgba(251,191,36,0.9);
  }

  .quickview-badge {
    position: absolute;
    top: 14px; left: 14px;
    z-index: 3;
    background: linear-gradient(135deg, #b45309, #f59e0b 55%, #fde68a);
    background-size: 200% 200%;
    color: #431407;
    padding: 6px 13px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 800;
    border: 1px solid rgba(255,255,255,0.55);
    box-shadow: 0 6px 18px rgba(180,83,9,0.45);
    animation: qvDrift 6s ease-in-out infinite, qvBadgeFloat 4.5s ease-in-out infinite;
  }
  @keyframes qvBadgeFloat {
    0%, 100% { transform: translateY(0); }
    50%      { transform: translateY(-4px); }
  }
  .quickview-discount {
    position: absolute;
    top: 14px; right: 14px;
    z-index: 3;
    background: linear-gradient(135deg, #7c2d12, #ea580c 60%, #fbbf24);
    color: #fff;
    padding: 6px 12px;
    border-radius: 30px;
    font-size: 12px;
    font-weight: 800;
    border: 1px solid rgba(255,255,255,0.45);
    box-shadow: 0 6px 18px rgba(234,88,12,0.5);
    animation: qvPulse 2.4s ease-in-out infinite;
  }
  @keyframes qvPulse {
    0%, 100% { transform: scale(1);    box-shadow: 0 6px 18px rgba(234,88,12,0.45); }
    50%      { transform: scale(1.08); box-shadow: 0 8px 26px rgba(251,191,36,0.75); }
  }

  /* ===== Details ===== */
  /* Gilded title — light travels through the letters */
  .quickview-title {
    font-size: clamp(1.3rem, 3vw, 1.75rem);
    letter-spacing: -0.4px;
    background: linear-gradient(100deg, #4c1d95 8%, #b45309 28%, #ea580c 46%, #b45309 64%, #4c1d95 88%);
    background-size: 260% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    animation: qvGild 7s linear infinite;
  }
  @keyframes qvGild {
    from { background-position: 260% 0; }
    to   { background-position: -60% 0; }
  }
  .quickview-stars {
    font-size: 0.9rem;
    letter-spacing: 1px;
  }
  .quickview-cat {
    border: 1px solid #e9d5ff !important;
    background: linear-gradient(135deg, #faf5ff, #fff7ed) !important;
    color: #6b21a8 !important;
    border-radius: 30px;
    padding: 6px 13px;
    font-weight: 700;
    letter-spacing: 0.3px;
  }
  .quickview-price {
    font-size: 1.95rem;
    background: linear-gradient(120deg, #7c2d12, #ea580c 50%, #f59e0b);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    filter: drop-shadow(0 2px 10px rgba(234,88,12,0.28));
  }
  .quickview-save {
    padding: 3px 12px;
    border-radius: 30px;
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    border: 1px solid #fcd34d;
    color: #92400e;
    font-size: 0.8rem;
    font-weight: 800;
    animation: qvSaveGlow 3s ease-in-out infinite;
  }
  @keyframes qvSaveGlow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(251,191,36,0.55); }
    50%      { box-shadow: 0 0 16px 3px rgba(251,191,36,0.5); }
  }
  .quickview-desc {
    line-height: 1.65;
    margin-bottom: 0.75rem;
  }

  /* Seller highlights — each line slides in behind the one before it */
  .qv-highlights {
    list-style: none;
    margin: 0 0 0.85rem;
    padding: 0;
    display: grid;
    gap: 6px;
  }
  .qv-highlights li {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    font-size: 0.82rem;
    font-weight: 600;
    color: #57534e;
    line-height: 1.4;
    animation: qvSlideIn 0.45s ease both;
    animation-delay: calc(var(--i, 0) * 80ms);
  }
  @keyframes qvSlideIn {
    from { opacity: 0; transform: translateX(-8px); }
    to   { opacity: 1; transform: none; }
  }
  .qv-highlight-tick {
    flex-shrink: 0;
    font-size: 0.72rem;
    line-height: 1.7;
    color: #f59e0b;
    text-shadow: 0 0 10px rgba(251,191,36,0.8);
  }

  /* Availability */
  .qv-stock {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    align-self: flex-start;
    padding: 5px 13px;
    border-radius: 30px;
    font-size: 0.78rem;
    font-weight: 800;
    border: 1px solid transparent;
  }
  .qv-stock-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
    animation: qvBeacon 1.9s ease-in-out infinite;
  }
  @keyframes qvBeacon {
    0%, 100% { box-shadow: 0 0 0 0 currentColor;   opacity: 1; }
    70%      { box-shadow: 0 0 0 6px transparent;  opacity: 0.55; }
  }
  .qv-stock.is-in {
    background: linear-gradient(135deg, #f0fdf4, #ecfdf5);
    border-color: #bbf7d0;
    color: #15803d;
  }
  .qv-stock.is-low {
    background: linear-gradient(135deg, #fffbeb, #fff7ed);
    border-color: #fcd34d;
    color: #b45309;
  }
  .qv-stock.is-out {
    background: #f5f5f4;
    border-color: #e7e5e4;
    color: #78716c;
  }
  .qv-stock.is-out .qv-stock-dot {
    animation: none;
  }

  /* Reassurance row, sitting at the foot of the column */
  .qv-perks {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding-top: 14px;
    border-top: 1px dashed rgba(167,139,250,0.45);
  }
  .qv-perk {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    border-radius: 30px;
    background: rgba(255,255,255,0.72);
    border: 1px solid #f3e8ff;
    color: #6b21a8;
    font-size: 0.72rem;
    font-weight: 700;
    white-space: nowrap;
    backdrop-filter: blur(4px);
    transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  }
  .qv-perk:hover {
    transform: translateY(-2px);
    border-color: #fcd34d;
    box-shadow: 0 8px 18px rgba(124,58,237,0.16), 0 0 14px rgba(251,191,36,0.35);
  }
  .qv-perk-icon {
    font-size: 0.9rem;
    line-height: 1;
  }

  /* ===== Buttons ===== */
  .quickview-btn {
    position: relative;
    overflow: hidden;
    border-radius: 11px;
    padding: 7px 14px;
    font-size: 0.82rem;
    line-height: 1.35;
    /* Keep each label on one line so the row stays compact */
    white-space: nowrap;
    transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
  }
  .quickview-btn:hover {
    transform: translateY(-2px);
  }
  .qv-btn-primary,
  .qv-btn-primary:hover,
  .qv-btn-primary:focus,
  .qv-btn-primary:active {
    border: none;
    color: #fff;
    background: linear-gradient(120deg, #4c1d95, #7c2d12 45%, #ea580c 75%, #f59e0b);
    background-size: 220% 220%;
    box-shadow: 0 12px 28px rgba(76,29,149,0.38);
    animation: qvDrift 7s ease-in-out infinite;
  }
  .qv-btn-primary:hover {
    box-shadow: 0 16px 36px rgba(234,88,12,0.5), 0 0 26px rgba(251,191,36,0.45);
  }
  /* Light sweeps across the button on hover */
  .qv-btn-shine {
    position: absolute;
    top: -60%;
    bottom: -60%;
    left: -45%;
    width: 40%;
    pointer-events: none;
    background: linear-gradient(100deg, transparent, rgba(255,255,255,0.55), transparent);
    transform: rotate(16deg);
  }
  .qv-btn-primary:hover .qv-btn-shine {
    animation: qvBtnShine 0.85s ease;
  }
  @keyframes qvBtnShine {
    to { left: 120%; }
  }
  .qv-btn-edit,
  .qv-btn-edit:hover,
  .qv-btn-edit:focus {
    border: none;
    color: #431407;
    background: linear-gradient(135deg, #fbbf24, #fde68a);
    box-shadow: 0 10px 24px rgba(180,83,9,0.32);
  }
  .qv-btn-ghost,
  .qv-btn-ghost:focus {
    border: 1px solid #e9d5ff;
    color: #6b21a8;
    background: rgba(255,255,255,0.7);
    backdrop-filter: blur(4px);
  }
  .qv-btn-ghost:hover {
    border-color: #c4b5fd;
    color: #4c1d95;
    background: #faf5ff;
    box-shadow: 0 10px 22px rgba(124,58,237,0.18);
  }

  /* ===== Suggestions ===== */
  .quickview-suggest {
    border-top: 1px solid transparent;
    border-image: linear-gradient(90deg, transparent, rgba(251,191,36,0.7), rgba(167,139,250,0.7), transparent) 1;
  }
  .quickview-suggest-head {
    background: linear-gradient(100deg, #4c1d95, #ea580c 50%, #b45309);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
    letter-spacing: 0.3px;
  }
  .quickview-suggest-card {
    display: block;
    text-decoration: none;
    color: inherit;
    background: rgba(255,255,255,0.75);
    border: 1px solid #f3e8ff;
    border-radius: 14px;
    padding: 8px;
    backdrop-filter: blur(4px);
    transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
    /* Cards fade up in sequence so the shelf fills in rather than pops */
    animation: qvSuggestRise 0.5s ease both;
    animation-delay: calc(var(--i, 0) * 90ms);
  }
  @keyframes qvSuggestRise {
    from { opacity: 0; transform: translateY(12px); }
    to   { opacity: 1; transform: none; }
  }
  .quickview-suggest-card:hover {
    transform: translateY(-5px);
    border-color: #fcd34d;
    box-shadow: 0 14px 28px rgba(124,58,237,0.20), 0 0 20px rgba(251,191,36,0.35);
  }
  .quickview-suggest-img {
    width: 100%;
    height: 90px;
    object-fit: cover;
    border-radius: 10px;
    transition: transform 0.35s ease;
  }
  .quickview-suggest-card:hover .quickview-suggest-img {
    transform: scale(1.06);
  }
  .quickview-suggest-name {
    font-size: 0.78rem;
    font-weight: 600;
    margin-top: 6px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .quickview-suggest-price {
    font-size: 0.88rem;
    font-weight: 800;
    background: linear-gradient(120deg, #7c2d12, #ea580c);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  @media (prefers-reduced-motion: reduce) {
    .quickview-modal, .quickview-modal::after, .qv-aura, .qv-stars, .qv-spark,
    .qv-img-rays, .qv-slide-on, .qv-img-sheen, .qv-img-glow,
    .quickview-badge, .quickview-discount, .quickview-title, .quickview-save,
    .qv-highlights li, .qv-stock-dot, .qv-perk,
    .qv-btn-primary, .qv-btn-primary:hover .qv-btn-shine,
    .quickview-suggest-card, .quickview-suggest-img, .quickview-btn,
    .quickview-dot, .qv-head .btn-close {
      transition: none;
      animation: none;
    }
  }
`;

export default QuickViewModal;
